import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Icon,
  Spinner,
  Center,
  VStack,
} from "@chakra-ui/react";
import { TimeIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useGlobalContext } from "@root/context/global-context";
import { useNavigate } from "react-router-dom";
import { FILE_TYPE, PAGINATION_DEFAULTS, ROUTE_NAMES } from "@root/constants";
import { ListIcon } from "@root/assets/icons/view-report";
import { Paginate } from "@root/components/pagination";
import { formatDate } from "@root/utils/date-helper";

interface ReportListProps {
  reports: any[];
  count: number;
  page: number;
  onPageChange: (newPage: number) => void;
  showPagination?: boolean;
  isExtraction?: boolean;
}

const ReportList: React.FC<ReportListProps> = ({
  reports,
  count,
  page,
  onPageChange,
  isExtraction = false,
  showPagination = true,
}) => {
  const { t } = useTranslation("common");
  const { reportsLoading } = useGlobalContext();
  const navigate = useNavigate();

  const handleViewReport = (file: any) => {
    if (isExtraction) {
      navigate(`${ROUTE_NAMES.EXTRACTION_REVIEW}/${file.id}`);
    } else if (file?.type === FILE_TYPE.FINISHING_MATERIAL_REQUEST) {
      navigate(`${ROUTE_NAMES.MATERIAL_CAMPARISON}/${file.id}`);
    } else {
      navigate(`${ROUTE_NAMES.PERMIT_REVIEW}/${file.id}`);
    }
  };

  if (reportsLoading) {
    return (
      <Center p={4}>
        <Spinner size="lg" color="blue.500" />
      </Center>
    );
  }

  return (
    <>
      {/* Extraction Reports - with files array */}
      {isExtraction &&
        reports?.map((report, index: number) => (
          <Box
            key={report?.id ?? index}
            padding="20px"
            mb={3}
            position="relative"
            _after={{
              content: '""',
              position: "absolute",
              bottom: 0,
              left: "30px",
              right: "30px",
              bg: "#E6E6E6",
              height: "0.5px",
            }}
          >
            <Flex justify="space-between" align="flex-start">
              {/* Left side - Files list */}
              <VStack align="flex-start" spacing={2} flex={1}>
                {report?.files?.map((file: any, fileIndex: number) => (
                  <Box key={file?.id ?? fileIndex}>
                    <Text fontWeight="600" noOfLines={2} wordBreak="break-word">
                      {file?.fileName_key || file?.fileName || file?.name}
                    </Text>
                  </Box>
                ))}
                {/* Show date once for the report */}
                <Flex align="center" gap={1} color="gray.500" fontSize="sm">
                  <Icon as={TimeIcon} />
                  <Text>
                    {report?.createdAt || report?.created_at
                      ? formatDate(
                          report?.createdAt || report?.created_at,
                          "YYYY-MM-DD HH:mm:ss"
                        )
                      : "-"}
                  </Text>
                </Flex>
              </VStack>

              {/* Right side - Single View Report button */}
              <Button
                color="black"
                variant="outline"
                bg="#EBEBEB"
                size="sm"
                onClick={() => handleViewReport(report)}
                gap={2}
                flexShrink={0}
              >
                {t("dashboard.viewReport")}
                <ListIcon />
              </Button>
            </Flex>
          </Box>
        ))}

      {/* Regular Reports */}
      {!isExtraction &&
        reports?.map((r, index: number) => (
          <Box
            key={r?.id ?? index}
            padding="20px"
            mb={3}
            position="relative"
            _after={{
              content: '""',
              position: "absolute",
              bottom: 0,
              left: "30px",
              right: "30px",
              bg: "#E6E6E6",
              height: "0.5px",
            }}
          >
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontWeight="600" noOfLines={2} wordBreak="break-word">
                  {r?.fileName_key}
                </Text>
                <Flex align="center" gap={1} color="gray.500" fontSize="sm">
                  <Icon as={TimeIcon} />
                  <Text>
                    {r?.createdAt
                      ? formatDate(r.createdAt, "YYYY-MM-DD HH:mm:ss")
                      : "-"}
                  </Text>
                </Flex>
              </Box>
              <Button
                color="black"
                variant="outline"
                bg="#EBEBEB"
                size="sm"
                onClick={() => handleViewReport(r)}
                gap={2}
              >
                {t("dashboard.viewReport")}
                <ListIcon />
              </Button>
            </Flex>
          </Box>
        ))}

      {showPagination && (
        <Flex width="100%" justifyContent={"center"}>
          <Paginate
            page={page}
            count={count}
            pageSize={PAGINATION_DEFAULTS.PAGESIZE}
            onPageChange={onPageChange}
          />
        </Flex>
      )}
    </>
  );
};

export default ReportList;
